import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Image, Users, Loader2, Package } from 'lucide-react';

import { PhotoCard } from '@/components/gallery/PhotoCard';
import { BundleOfferCard } from '@/components/gallery/BundleOfferCard';
import { PhotoPreviewDialog } from '@/components/gallery/PhotoPreviewDialog';
import { logError } from '@/lib/logger';

import { API } from '@/lib/apiBase';

export default function PhotoGallery() {
  const { classId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const { addToCart, removeFromCart, isInCart, addPrintItem } = useCart();
  const { t } = useLanguage();

  const [photos, setPhotos] = useState([]);
  const [classes, setClasses] = useState([]);
  const [printFormats, setPrintFormats] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selectedClass, setSelectedClass] = useState(classId || 'all');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { ...getAuthHeaders() };
        const [classesRes, photosRes, formatsRes, bundlesRes] = await Promise.all([
          fetch(`${API}/classes`, { headers, credentials: 'include' }),
          fetch(`${API}/photos${selectedClass !== 'all' ? `?class_id=${selectedClass}` : ''}`, { headers, credentials: 'include' }),
          fetch(`${API}/print-formats`, { headers, credentials: 'include' }),
          fetch(`${API}/bundles`, { headers, credentials: 'include' })
        ]);
        if (classesRes.ok) setClasses(await classesRes.json());
        if (photosRes.ok) setPhotos(await photosRes.json());
        if (formatsRes.ok) setPrintFormats(await formatsRes.json());
        if (bundlesRes.ok) setBundles(await bundlesRes.json());
      } catch (error) {
        logError('PhotoGallery.Errorfetchingdata', error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedClass, getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleCart = (photo) => {
    if (isInCart(photo.photo_id)) {
      removeFromCart(photo.photo_id);
      toast.info(t('cart.removeFromCart'));
    } else {
      addToCart(photo);
      toast.success(t('gallery.addToCart'));
    }
  };

  const handleAddPrint = (photo) => {
    if (!selectedFormat) {
      toast.error(t('prints.selectFormat'));
      return;
    }
    const format = printFormats.find(f => f.format_id === selectedFormat);
    if (!format) return;
    addPrintItem(photo, format);
    toast.success(`${t('prints.addPrint')}: ${format.name}`);
    setSelectedFormat('');
  };

  const handleDownload = async (photo) => {
    setDownloading(photo.photo_id);
    try {
      const headers = { ...getAuthHeaders() };
      const response = await fetch(`${API}/photos/${photo.photo_id}/download`, {
        headers, credentials: 'include'
      });
      if (!response.ok) {
        toast.error(t('common.error'));
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photo_${photo.photo_id}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('common.download'));
    } catch (error) {
      logError('PhotoGallery.Downloaderror', error);
      toast.error(t('common.error'));
    } finally {
      setDownloading(null);
    }
  };

  const canDownloadFree = (photo) => {
    if (user?.role === 'teacher' && (photo.is_class_photo || photo.is_teacher_photo)) return true;
    if (user?.role === 'admin') return true;
    return false;
  };

  const classPhotos = photos.filter(p => p.is_class_photo);
  const individualPhotos = photos.filter(p => !p.is_class_photo);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="photo-gallery">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('gallery.title')}</h1>
          <p className="text-muted-foreground">{t('gallery.description')}</p>
        </div>

        {classes.length > 0 && (
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-[200px] bg-card border-border" data-testid="class-filter">
              <SelectValue placeholder={t('gallery.filterByClass')} />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">{t('gallery.allClasses')}</SelectItem>
              {classes.map((cls) => (
                <SelectItem key={cls.class_id} value={cls.class_id}>
                  {cls.name} ({cls.year})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-24">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Image className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('gallery.noPhotos')}</h3>
          <p className="text-muted-foreground">
            {user?.role === 'parent' ? t('gallery.noPhotosParent') : t('gallery.noPhotosOther')}
          </p>
        </div>
      ) : (
        <>
          {bundles.length > 0 && user?.role !== 'teacher' && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Package className="w-5 h-5 text-green-500" />
                <h2 className="text-xl font-bold">{t('bundles.buyAllPhotos')}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {bundles.map((bundle) => {
                  const key = bundle.student_id ? `${bundle.bundle_id}_${bundle.student_id}` : bundle.bundle_id;
                  return <BundleOfferCard key={key} bundle={bundle} />;
                })}
              </div>
            </section>
          )}

          {classPhotos.length > 0 && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold">{t('gallery.classPhotos')}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {classPhotos.map((photo, idx) => (
                  <PhotoCard
                    key={photo.photo_id}
                    photo={photo} isClassPhoto index={idx}
                    isInCart={isInCart} canDownloadFree={canDownloadFree} downloading={downloading}
                    onSelect={(p) => { setSelectedPhoto(p); setSelectedFormat(''); }}
                    onToggleCart={handleToggleCart}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            </section>
          )}

          {individualPhotos.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-6">
                <Image className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold">{t('gallery.individualPhotos')}</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {individualPhotos.map((photo, idx) => (
                  <PhotoCard
                    key={photo.photo_id}
                    photo={photo} isClassPhoto={false} index={idx}
                    isInCart={isInCart} canDownloadFree={canDownloadFree} downloading={downloading}
                    onSelect={(p) => { setSelectedPhoto(p); setSelectedFormat(''); }}
                    onToggleCart={handleToggleCart}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <PhotoPreviewDialog
        photo={selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        isInCart={isInCart} canDownloadFree={canDownloadFree} downloading={downloading}
        onToggleCart={handleToggleCart}
        onDownload={handleDownload}
        printFormats={printFormats}
        selectedFormat={selectedFormat}
        setSelectedFormat={setSelectedFormat}
        onAddPrint={handleAddPrint}
      />
    </div>
  );
}
